import type { FormattingAction } from '../components/primitives/FormattingToolbar';

type SpliceSpec =
  | { kind: 'wrap'; before: string; after: string; placeholder: string }
  | { kind: 'prefix'; prefix: string; stripRe?: RegExp }
  | {
      kind: 'insert';
      text: string;
      caretSelection?: [number, number];
    };

const SYNTAX: Record<FormattingAction, SpliceSpec> = {
  bold: { kind: 'wrap', before: '**', after: '**', placeholder: 'text' },
  italic: { kind: 'wrap', before: '*', after: '*', placeholder: 'text' },
  h1: { kind: 'prefix', prefix: '# ' },
  h2: { kind: 'prefix', prefix: '## ' },
  h3: { kind: 'prefix', prefix: '### ' },
  bullet: { kind: 'prefix', prefix: '- ' },
  ordered: {
    kind: 'prefix',
    prefix: '1. ',
    stripRe: /^\d+\.\s/
  },
  quote: { kind: 'prefix', prefix: '> ' },
  code: { kind: 'wrap', before: '```\n', after: '\n```', placeholder: 'code' },
  link: {
    kind: 'wrap',
    before: '[',
    after: '](https://)',
    placeholder: 'text'
  },
  divider: { kind: 'insert', text: '\n\n---\n\n' }
};

export type ActiveFormats = Partial<Record<FormattingAction, boolean>>;

function lineBlockBounds(value: string, start: number, end: number) {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = value.indexOf('\n', end);
  const blockEnd = lineEnd === -1 ? value.length : lineEnd;
  return { lineStart, blockEnd };
}

function isAsteriskPartOfBold(value: string, index: number): boolean {
  if (value[index] !== '*') return false;
  if (index > 0 && value[index - 1] === '*') return true;
  if (index + 1 < value.length && value[index + 1] === '*') return true;
  return false;
}

/** True when `before…after` between openStart and closeIdx is a single wrap (no nested open). */
function isValidWrapPair(
  value: string,
  openStart: number,
  closeIdx: number,
  before: string,
  _after: string,
  options?: { skipOpenAt?: (i: number) => boolean }
): boolean {
  const bl = before.length;
  const innerStart = openStart + bl;
  let pos = innerStart;
  while (pos <= closeIdx - bl) {
    if (value.slice(pos, pos + bl) !== before) {
      pos++;
      continue;
    }
    if (options?.skipOpenAt?.(pos)) {
      pos++;
      continue;
    }
    return false;
  }
  return true;
}

function findEnclosingWrap(
  value: string,
  start: number,
  end: number,
  before: string,
  after: string,
  options?: { skipOpenAt?: (i: number) => boolean; skipCloseAt?: (i: number) => boolean }
): { outerStart: number; outerEnd: number } | null {
  const bl = before.length;
  const al = after.length;

  if (
    start >= bl &&
    end <= value.length - al &&
    value.slice(start - bl, start) === before &&
    value.slice(end, end + al) === after &&
    !(options?.skipOpenAt?.(start - bl) ?? false) &&
    !(options?.skipCloseAt?.(end) ?? false) &&
    isValidWrapPair(value, start - bl, end, before, after, options)
  ) {
    return { outerStart: start - bl, outerEnd: end + al };
  }

  let best: { outerStart: number; outerEnd: number } | null = null;
  for (let i = 0; i <= start; i++) {
    if (value.slice(i, i + bl) !== before) continue;
    if (options?.skipOpenAt?.(i)) continue;
    let searchFrom = i + bl;
    while (searchFrom < value.length) {
      const closeIdx = value.indexOf(after, searchFrom);
      if (closeIdx === -1) break;
      searchFrom = closeIdx + 1;
      if (options?.skipCloseAt?.(closeIdx)) continue;
      if (!isValidWrapPair(value, i, closeIdx, before, after, options)) continue;
      const outerEnd = closeIdx + al;
      if (outerEnd < end) continue;
      const candidate = { outerStart: i, outerEnd };
      if (
        !best ||
        candidate.outerEnd - candidate.outerStart <
          best.outerEnd - best.outerStart
      ) {
        best = candidate;
      }
    }
  }
  return best;
}

function lineIsFullyWrapped(
  line: string,
  before: string,
  after: string,
  options?: { skipOpenAt?: (i: number) => boolean; skipCloseAt?: (i: number) => boolean }
): boolean {
  const bl = before.length;
  const al = after.length;
  if (line.length < bl + al) return false;
  if (line.slice(0, bl) !== before || line.slice(line.length - al) !== after) {
    return false;
  }
  if (options?.skipOpenAt?.(0) ?? false) return false;
  if (options?.skipCloseAt?.(line.length - al) ?? false) return false;
  return true;
}

function unwrapLineWrap(line: string, before: string, after: string): string {
  return line.slice(before.length, line.length - after.length);
}

/** Per-line wrap in the selection's line block (e.g. `**a**\\n**b**`). */
function applyPerLineWrapBlock(
  value: string,
  start: number,
  end: number,
  before: string,
  after: string,
  options?: { skipOpenAt?: (i: number) => boolean; skipCloseAt?: (i: number) => boolean }
): { text: string; selectionStart: number; selectionEnd: number } | null {
  const { lineStart, blockEnd } = lineBlockBounds(value, start, end);
  const block = value.slice(lineStart, blockEnd);
  const lines = block.split('\n');
  const nonEmpty = lines.filter((ln) => ln.length > 0);
  if (nonEmpty.length === 0) return null;

  const wrappedFlags = lines.map((ln) =>
    ln.length === 0 ? true : lineIsFullyWrapped(ln, before, after, options)
  );
  const allWrapped = lines.every(
    (ln, idx) => ln.length === 0 || wrappedFlags[idx]
  );

  if (allWrapped) {
    const nextLines = lines.map((ln) =>
      ln.length === 0 ? ln : unwrapLineWrap(ln, before, after)
    );
    const inserted = nextLines.join('\n');
    const text = value.slice(0, lineStart) + inserted + value.slice(blockEnd);
    return {
      text,
      selectionStart: lineStart,
      selectionEnd: lineStart + inserted.length
    };
  }

  const someWrapped = lines.some(
    (ln, idx) => ln.length > 0 && wrappedFlags[idx]
  );
  const someBare = lines.some(
    (ln, idx) => ln.length > 0 && !wrappedFlags[idx]
  );
  if (someWrapped && someBare) {
    const nextLines = lines.map((ln, idx) => {
      if (ln.length === 0) return ln;
      return wrappedFlags[idx] ? ln : before + ln + after;
    });
    const inserted = nextLines.join('\n');
    const text = value.slice(0, lineStart) + inserted + value.slice(blockEnd);
    return {
      text,
      selectionStart: lineStart,
      selectionEnd: lineStart + inserted.length
    };
  }

  return null;
}

function isPerLineWrapActive(
  value: string,
  start: number,
  end: number,
  before: string,
  after: string,
  options?: { skipOpenAt?: (i: number) => boolean; skipCloseAt?: (i: number) => boolean }
): boolean {
  const { lineStart, blockEnd } = lineBlockBounds(value, start, end);
  const lines = value.slice(lineStart, blockEnd).split('\n');
  const nonEmpty = lines.filter((ln) => ln.length > 0);
  if (nonEmpty.length === 0) return false;
  return lines.every(
    (ln) => ln.length === 0 || lineIsFullyWrapped(ln, before, after, options)
  );
}

function unwrapEnclosing(
  value: string,
  enclosing: { outerStart: number; outerEnd: number },
  before: string,
  after: string
): { text: string; selectionStart: number; selectionEnd: number } {
  const inner = value.slice(
    enclosing.outerStart + before.length,
    enclosing.outerEnd - after.length
  );
  const text =
    value.slice(0, enclosing.outerStart) +
    inner +
    value.slice(enclosing.outerEnd);
  const selStart = enclosing.outerStart;
  const selEnd = selStart + inner.length;
  return { text, selectionStart: selStart, selectionEnd: selEnd };
}

function findEnclosingCodeFence(
  value: string,
  start: number,
  end: number
): { outerStart: number; outerEnd: number } | null {
  const open = '```\n';
  const close = '\n```';
  const direct = findEnclosingWrap(value, start, end, open, close);
  if (direct) return direct;

  let best: { outerStart: number; outerEnd: number } | null = null;
  let searchFrom = 0;
  while (searchFrom < value.length) {
    const o = value.indexOf(open, searchFrom);
    if (o === -1) break;
    const c = value.indexOf(close, o + open.length);
    if (c === -1) break;
    const outerEnd = c + close.length;
    if (o <= start && outerEnd >= end) {
      const candidate = { outerStart: o, outerEnd };
      if (
        !best ||
        candidate.outerEnd - candidate.outerStart <
          best.outerEnd - best.outerStart
      ) {
        best = candidate;
      }
    }
    searchFrom = o + open.length;
  }
  return best;
}

function findEnclosingLink(
  value: string,
  start: number,
  end: number
): { outerStart: number; outerEnd: number; label: string } | null {
  const re = /\[([^\]]*)\]\(([^)]*)\)/g;
  let match: RegExpExecArray | null;
  let best: { outerStart: number; outerEnd: number; label: string } | null =
    null;
  while ((match = re.exec(value)) !== null) {
    const outerStart = match.index;
    const outerEnd = match.index + match[0].length;
    if (outerStart <= start && outerEnd >= end) {
      const candidate = {
        outerStart,
        outerEnd,
        label: match[1] ?? ''
      };
      if (
        !best ||
        candidate.outerEnd - candidate.outerStart <
          best.outerEnd - best.outerStart
      ) {
        best = candidate;
      }
    }
  }
  return best;
}

function lineHasPrefix(line: string, prefix: string, stripRe?: RegExp): boolean {
  if (line.startsWith(prefix)) return true;
  if (stripRe) return stripRe.test(line);
  return false;
}

function stripLinePrefix(line: string, prefix: string, stripRe?: RegExp): string {
  if (line.startsWith(prefix)) return line.slice(prefix.length);
  if (stripRe) return line.replace(stripRe, '');
  return line;
}

function applyPrefixBlock(
  value: string,
  lineStart: number,
  blockEnd: number,
  prefix: string,
  stripRe?: RegExp
): { text: string; selectionStart: number; selectionEnd: number } {
  const block = value.slice(lineStart, blockEnd);
  const lines = block.split('\n');
  const allHave = lines.every((ln) => lineHasPrefix(ln, prefix, stripRe));
  const nextLines = allHave
    ? lines.map((ln) => stripLinePrefix(ln, prefix, stripRe))
    : lines.map((ln) =>
        lineHasPrefix(ln, prefix, stripRe) ? ln : prefix + ln
      );
  const inserted = nextLines.join('\n');
  const text = value.slice(0, lineStart) + inserted + value.slice(blockEnd);
  return {
    text,
    selectionStart: lineStart,
    selectionEnd: lineStart + inserted.length
  };
}

function applyWrap(
  value: string,
  start: number,
  end: number,
  spec: Extract<SpliceSpec, { kind: 'wrap' }>,
  action: FormattingAction
): { text: string; selectionStart: number; selectionEnd: number } {
  const italicOpts =
    action === 'italic'
      ? {
          skipOpenAt: (i: number) => isAsteriskPartOfBold(value, i),
          skipCloseAt: (i: number) => isAsteriskPartOfBold(value, i)
        }
      : undefined;

  if (action === 'code') {
    const fence = findEnclosingCodeFence(value, start, end);
    if (fence) {
      const inner = value.slice(
        fence.outerStart + spec.before.length,
        fence.outerEnd - spec.after.length
      );
      const text =
        value.slice(0, fence.outerStart) +
        inner +
        value.slice(fence.outerEnd);
      const selStart = fence.outerStart;
      const selEnd = selStart + inner.length;
      return { text, selectionStart: selStart, selectionEnd: selEnd };
    }
  } else if (action === 'link') {
    const link = findEnclosingLink(value, start, end);
    if (link) {
      const text =
        value.slice(0, link.outerStart) +
        link.label +
        value.slice(link.outerEnd);
      const selStart = link.outerStart;
      const selEnd = selStart + link.label.length;
      return { text, selectionStart: selStart, selectionEnd: selEnd };
    }
  } else if (action === 'italic') {
    if (findEnclosingWrap(value, start, end, '**', '**')) {
      return {
        text: value,
        selectionStart: start,
        selectionEnd: end
      };
    }
  }

  if (action === 'bold' || action === 'italic') {
    const wrapOpts = action === 'italic' ? italicOpts : undefined;
    const enclosing = findEnclosingWrap(
      value,
      start,
      end,
      spec.before,
      spec.after,
      wrapOpts
    );
    if (enclosing) {
      return unwrapEnclosing(value, enclosing, spec.before, spec.after);
    }
    const perLine = applyPerLineWrapBlock(
      value,
      start,
      end,
      spec.before,
      spec.after,
      wrapOpts
    );
    if (perLine) return perLine;
  }

  const sel = value.slice(start, end) || spec.placeholder;
  const inserted = spec.before + sel + spec.after;
  const text = value.slice(0, start) + inserted + value.slice(end);
  const caretEnd = start + spec.before.length + sel.length;
  return {
    text,
    selectionStart: caretEnd,
    selectionEnd: caretEnd
  };
}

export function applyFormattingAction(
  value: string,
  start: number,
  end: number,
  action: FormattingAction
): { value: string; selectionStart: number; selectionEnd: number } {
  const spec = SYNTAX[action];
  if (!spec) {
    return { value, selectionStart: start, selectionEnd: end };
  }

  if (spec.kind === 'insert') {
    const inserted = spec.text;
    const caretPos = start + (spec.caretSelection?.[1] ?? inserted.length);
    return {
      value: value.slice(0, start) + inserted + value.slice(end),
      selectionStart: caretPos,
      selectionEnd: caretPos
    };
  }

  if (spec.kind === 'prefix') {
    const { lineStart, blockEnd } = lineBlockBounds(value, start, end);
    const { text, selectionStart, selectionEnd } = applyPrefixBlock(
      value,
      lineStart,
      blockEnd,
      spec.prefix,
      spec.stripRe
    );
    return { value: text, selectionStart, selectionEnd };
  }

  const { text, selectionStart, selectionEnd } = applyWrap(
    value,
    start,
    end,
    spec,
    action
  );
  return { value: text, selectionStart, selectionEnd };
}

function lineAt(value: string, pos: number): string {
  const { lineStart, blockEnd } = lineBlockBounds(value, pos, pos);
  return value.slice(lineStart, blockEnd);
}

function detectPrefixActive(line: string, action: FormattingAction): boolean {
  switch (action) {
    case 'h1':
      return /^# /.test(line) && !/^##/.test(line);
    case 'h2':
      return /^## /.test(line) && !/^###/.test(line);
    case 'h3':
      return /^### /.test(line);
    case 'bullet':
      return /^[-*] /.test(line);
    case 'ordered':
      return /^\d+\.\s/.test(line);
    case 'quote':
      return /^> /.test(line);
    default:
      return false;
  }
}

export type ListEnterResult = {
  handled: boolean;
  value: string;
  selectionStart: number;
  selectionEnd: number;
};

/** Enter on a list line: continue with next item or exit when the item is empty. */
export function handleListEnter(
  value: string,
  selectionStart: number,
  selectionEnd: number
): ListEnterResult {
  const noop: ListEnterResult = {
    handled: false,
    value,
    selectionStart,
    selectionEnd
  };
  if (selectionStart !== selectionEnd) return noop;

  const pos = selectionStart;
  const lineStart = value.lastIndexOf('\n', pos - 1) + 1;
  const nextNl = value.indexOf('\n', pos);
  const lineEnd = nextNl === -1 ? value.length : nextNl;
  const line = value.slice(lineStart, lineEnd);

  const bulletMatch = line.match(/^([-*]) (.*)$/);
  const orderedMatch = line.match(/^(\d+)\. (.*)$/);

  let prefix: string;
  let orderedNum = 0;

  if (bulletMatch) {
    prefix = `${bulletMatch[1]} `;
  } else if (orderedMatch) {
    orderedNum = parseInt(orderedMatch[1], 10);
    prefix = `${orderedMatch[1]}. `;
  } else {
    return noop;
  }

  const caretInLine = pos - lineStart;
  const contentAfterPrefix = line.slice(prefix.length);
  const beforeCaret = line.slice(0, caretInLine);
  const afterCaret = line.slice(caretInLine);

  if (contentAfterPrefix.trim() === '') {
    const nextValue =
      value.slice(0, lineStart) + value.slice(lineEnd);
    return {
      handled: true,
      value: nextValue,
      selectionStart: lineStart,
      selectionEnd: lineStart
    };
  }

  const nextPrefix = bulletMatch
    ? prefix
    : `${orderedNum + 1}. `;
  const nextValue =
    value.slice(0, lineStart) +
    beforeCaret +
    '\n' +
    nextPrefix +
    afterCaret +
    value.slice(lineEnd);
  const caret = lineStart + beforeCaret.length + 1 + nextPrefix.length;

  return {
    handled: true,
    value: nextValue,
    selectionStart: caret,
    selectionEnd: caret
  };
}

export function detectActiveFormats(
  value: string,
  start: number,
  end: number
): ActiveFormats {
  const active: ActiveFormats = {};
  const pos = start;

  const italicOpts = {
    skipOpenAt: (i: number) => isAsteriskPartOfBold(value, i),
    skipCloseAt: (i: number) => isAsteriskPartOfBold(value, i)
  };

  if (
    findEnclosingWrap(value, start, end, '**', '**') ||
    findEnclosingWrap(value, pos, pos, '**', '**') ||
    isPerLineWrapActive(value, start, end, '**', '**')
  ) {
    active.bold = true;
  }

  if (
    findEnclosingWrap(value, start, end, '*', '*', italicOpts) ||
    findEnclosingWrap(value, pos, pos, '*', '*', italicOpts) ||
    isPerLineWrapActive(value, start, end, '*', '*', italicOpts)
  ) {
    active.italic = true;
  }

  if (
    findEnclosingCodeFence(value, start, end) ||
    findEnclosingCodeFence(value, pos, pos)
  ) {
    active.code = true;
  }

  if (
    findEnclosingLink(value, start, end) ||
    findEnclosingLink(value, pos, pos)
  ) {
    active.link = true;
  }

  const line = lineAt(value, pos);
  for (const a of ['h1', 'h2', 'h3', 'bullet', 'ordered', 'quote'] as const) {
    if (detectPrefixActive(line, a)) active[a] = true;
  }

  return active;
}
